import React, { useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { UnidataTable, Table } from '~/components';
import { CUSTOMIZACAO_RAIZEN } from '~/constants/modules';
import { t, can } from '~/helpers';
import { getToastErrors } from '~/helpers/errorsRequest';
import { handleExportGeneric } from '~/helpers/exportReports';
import api from '~/services/api';
import {
  deleteUsuario,
  findUsuario,
  recordFiltering,
} from '~/store/modules/usuario/actions';

import Form from './form';
import * as S from './styles';

function UsuarioList() {
  const redux = useSelector((state) => state.usuario);

  const { modules } = useSelector((state) => state.user);

  const [loading, setLoading] = useState(false);
  const [userDetails, setUserDetails] = useState();

  const users = useMemo(
    () =>
      can(CUSTOMIZACAO_RAIZEN)
        ? [
            { label: 'U', value: t('s.cadastro.usuario.usuario') },
            { label: 'F', value: t('s.cadastro.usuario.frentista') },
          ]
        : [
            { label: 'U', value: t('s.cadastro.usuario.usuario') },
            { label: 'F', value: t('s.cadastro.usuario.frentista') },
            { label: 'A', value: t('s.cadastro.usuario.usuariofrentista') },
          ],
    []
  );

  const status = useMemo(
    () => [
      { label: 'L', value: t('s.geral.opcoes.liberado') },
      { label: 'B', value: t('s.geral.opcoes.bloqueado') },
      { label: 'C', value: t('s.geral.opcoes.cancelado') },
    ],
    []
  );

  const cellsLocalidades = useMemo(
    () => [
      { id: 'codigonomeposto', label: t('s.relatorio.geral.postocomboio') },
    ],
    []
  );

  const cellsAlarmes = useMemo(
    () => [
      {
        id: 'codigolabelsistema',
        label: t('s.configuracao.alarmesistema'),
        formatted: (value) => t(value),
      },
    ],
    []
  );

  const header = useMemo(
    () => {
      const tempArrayHeader = [
        {
          id: 'codigousuario',
          label: t('s.cadastro.usuario.codigousuario'),
          filter: { type: 'text' },
        },
        {
          id: 'nomeusuario',
          label: t('s.cadastro.usuario.nomeusuario'),
          filter: { type: 'text' },
        },
        {
          id: 'emailusuario',
          label: t('s.cadastro.usuario.emailusuario'),
          filter: { type: 'text' },
        },
        {
          id: 'nomeperfil',
          label: t('s.cadastro.usuario.idperfil'),
          filter: { type: 'text' },
        },
        {
          id: 'nomeperfilfrentista',
          label: t('s.cadastro.usuario.perfilfrentista.nomeperfilfrentista'),
          filter: { type: 'text' },
        },
        {
          id: 'tipousuario',
          label: t('s.cadastro.usuario.tipousuario'),
          formatted: (value) =>
            users.find((u) => u.label === value).value || '',
          filter: { type: 'select', options: users },
        },
        {
          id: 'statususuario',
          label: t('s.cadastro.usuario.statususuario'),
          formatted: (value) =>
            status.find((s) => s.label === value).value || '',
          filter: { type: 'select', options: status },
        },
      ];

      return tempArrayHeader;
    },

    [users, status]
  );

  const handleResponseDataItems = useCallback(async (id) => {
    try {
      if (id) {
        setLoading(true);
        await api
          .get(`/usuariosoperadores/detalhesalarmeselocalidades/${id}`)
          .then((response) =>
            setUserDetails({
              ...response.data,
              alarmes: response.data.alarmes.map((alarme) => ({
                ...alarme,
                codigolabelsistema: t(alarme.codigolabelsistema),
              })),
            })
          );
      }
    } catch (err) {
      getToastErrors(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fieldlist = [
    'codigousuario',
    'nomeusuario',
    'emailusuario',
    'nomeperfil',
    'nomeperfilfrentista',
    'tipousuario',
    'statususuario',
  ];

  const substituirHeader = {
    codigousuario: t('s.cadastro.usuario.codigousuario'),
    nomeusuario: t('s.cadastro.usuario.nomeusuario'),
    emailusuario: t('s.cadastro.usuario.emailusuario'),
    nomeperfil: t('s.cadastro.usuario.idperfil'),
    nomeperfilfrentista: t(
      's.cadastro.usuario.perfilfrentista.nomeperfilfrentista'
    ),
    tipousuario: t('s.cadastro.usuario.tipousuario'),
    statususuario: t('s.cadastro.usuario.statususuario'),
  };

  return (
    <S.Container>
      <UnidataTable
        fixed
        redux={redux}
        header={header}
        read={recordFiltering}
        find={findUsuario}
        remove={deleteUsuario}
        form={Form}
        onRow={(value) => handleResponseDataItems(value.idusuario)}
        keys="idusuario"
        detach="nomeusuario"
        maxHeight="60vh"
        pdf
        excel
        onExport={(formato) =>
          handleExportGeneric({
            endpoint: 'usuariosoperadores',
            formato,
            fields: fieldlist,
            headers: substituirHeader,
            redux,
            ordination: redux.params.ordination,
            orderfield: redux.params.orderfield,
            title: 'Relatorio de Usuarios',
            substituirValores: {
              statususuario: {
                L: t('s.geral.opcoes.liberado'),
                B: t('s.geral.opcoes.bloqueado'),
                C: t('s.geral.opcoes.cancelado'),
              },
              tipousuario: {
                U: t('s.cadastro.usuario.usuario'),
                F: t('s.cadastro.usuario.frentista'),
                A: t('s.cadastro.usuario.usuariofrentista'),
              },
            },
          })
        }
      />
      <S.Wrapper grids={2} style={{ flexWrap: 'wrap' }}>
        <Table
          isResponsive
          size="small"
          height="18rem"
          paginate={99}
          showPagination={false}
          cells={cellsLocalidades}
          data={userDetails?.localidades ?? []}
          loding={loading}
          style={{ maxWidth: '80%' }}
        />
        {modules.includes(22) && (
          <Table
            isResponsive
            size="small"
            height="18rem"
            paginate={99}
            showPagination={false}
            cells={cellsAlarmes}
            data={userDetails?.alarmes ?? []}
            loding={loading}
            style={{ maxWidth: '20%' }}
          />
        )}
      </S.Wrapper>
    </S.Container>
  );
}

export default UsuarioList;
